/**
 * Parlay Builder Core — Pure helper functions shared between web and mobile stores.
 *
 * These functions contain no platform-specific logic (no React, no Zustand, no DOM).
 * Both web (src/stores/parlayBuilderStore.ts) and mobile
 * (apps/mobile/src/stores/parlayBuilderStore.ts) import from here.
 */

import type { AnalysisConfig } from './analysisConfig';
import type { BuilderLeg } from './builderLeg';
import { americanToDecimal, decimalToAmerican, normalizeAmericanOdds } from './oddsUtils';
import type { ActionableSuggestion } from './parlayAnalysis';
import { formatStatLabel } from './statLabels';

/**
 * Generate a unique ID for a builder leg.
 */
export function generateLegId(): string {
  return `leg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Check if a leg with matching player + statType + line already exists.
 * Comparison is case-insensitive and trimmed.
 */
export function isDuplicateLeg(
  existingLegs: BuilderLeg[],
  player: string | null | undefined,
  statType: string,
  line: number | null,
): boolean {
  const normalizedPlayer = player?.toLowerCase()?.trim() ?? null;
  const normalizedStatType = statType.toLowerCase().trim();

  return existingLegs.some((l) => {
    const lPlayer = l.player?.toLowerCase()?.trim() ?? null;
    const lStatType = l.statType.toLowerCase().trim();
    return lPlayer === normalizedPlayer && lStatType === normalizedStatType && l.line === line;
  });
}

/**
 * Calculate combined American odds from multiple legs.
 * Converts each leg to decimal, multiplies, converts back to American.
 * Returns null if no legs or no valid odds.
 */
export function calculateCombinedOdds(legs: Array<{ odds?: string | null }>): string | null {
  if (legs.length === 0) return null;
  // Nothing to price if no leg carries usable odds (keeps the total blank rather
  // than fabricating one from all-default legs).
  if (!legs.some((leg) => normalizeAmericanOdds(leg.odds?.toString()) != null)) return null;

  const decimalProduct = legs.reduce((product, leg) => {
    // Normalize "Even"/"PK" and default a missing or unparseable leg to -110, rather
    // than dropping it (which understated the price) or letting a NaN poison the total.
    const normalized = normalizeAmericanOdds(leg.odds?.toString()) ?? '-110';
    const parsed = parseInt(normalized.replace('+', ''), 10);
    const decimal = americanToDecimal(Number.isNaN(parsed) ? -110 : parsed);
    return product * decimal;
  }, 1);

  if (decimalProduct === 1) return null;

  return decimalToAmerican(decimalProduct);
}

/**
 * Build a human-readable prop string from leg data.
 * Examples: "Over 25.5 pts", "LAL -3.5", "LAL ML"
 */
export function buildPropString(leg: BuilderLeg): string {
  if (leg.overUnder && leg.line !== null) {
    return `${leg.overUnder} ${leg.line} ${formatStatLabel(leg.statType)}`;
  }
  if (leg.line !== null) {
    return `${leg.team} ${leg.line > 0 ? '+' : ''}${leg.line}`;
  }
  return `${leg.team} ML`;
}

/**
 * The builder leg an analyzer suggestion targets: by `legId` when it carries one (no match means
 * no target), else by `legIndex`. -1 when it targets nothing.
 */
export function suggestionTargetIndex(
  legs: BuilderLeg[],
  suggestion: ActionableSuggestion,
): number {
  const index = suggestion.legId
    ? legs.findIndex((l) => l.id === suggestion.legId)
    : suggestion.legIndex;
  return index >= 0 && index < legs.length ? index : -1;
}

/** What the analyzer ran with, recorded on a leg it swaps in or re-lines. */
export interface AnalyzerSuggestionContext {
  /** The user's settings at the time, kept as the leg's `analysisConfig` snapshot. */
  analysisConfig: AnalysisConfig | null;
  /** The toggles that shaped the analysis, from the analyze response. */
  factorsUsed: AnalysisConfig | null;
}

/** The builder's legs after applying an analyzer suggestion; unchanged when it targets nothing. */
export function applyAnalyzerSuggestion(
  legs: BuilderLeg[],
  suggestion: ActionableSuggestion,
  context: AnalyzerSuggestionContext,
): BuilderLeg[] {
  const targetIndex = suggestionTargetIndex(legs, suggestion);
  if (targetIndex < 0) return legs;

  switch (suggestion.type) {
    case 'remove':
      return legs.filter((_, i) => i !== targetIndex);

    case 'swap': {
      const { swapTo } = suggestion;
      if (!swapTo) return legs;
      const oldLeg = legs[targetIndex];
      const newLeg: BuilderLeg = {
        id: generateLegId(),
        player: swapTo.playerName,
        playerId: swapTo.playerId,
        team: swapTo.team,
        statType: swapTo.propType,
        line: swapTo.line,
        overUnder: swapTo.overUnder,
        odds: swapTo.odds,
        sportsbook: oldLeg.sportsbook, // Keep same sportsbook
        marketKey: swapTo.marketKey,
        gameId: oldLeg.gameId, // Keep same game
        gameDate: oldLeg.gameDate,
        opponentTeam: oldLeg.opponentTeam,
        source: 'ai', // Mark as AI suggestion
        provenance: 'analyzer',
        factorsUsed: context.factorsUsed,
        analysisConfig: context.analysisConfig,
        leagueKey: oldLeg.leagueKey, // Inherit league from original leg
      };
      return legs.map((l, i) => (i === targetIndex ? newLeg : l));
    }

    case 'adjust_line': {
      const { newLine } = suggestion;
      if (newLine === undefined) return legs;
      // A moved line is no longer the generated pick, so it drops the generated id
      return legs.map((l, i) =>
        i === targetIndex
          ? {
              ...l,
              line: newLine,
              provenance: 'analyzer',
              predictionId: undefined,
              factorsUsed: context.factorsUsed,
            }
          : l,
      );
    }
  }
}
